import { collection, getDocs, } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { LogFunction } from './types';
import type { Flavor, Attribute, Brand, Drink, FirestoreLocation, Sighting, Review, DrinkFlavor, DrinkAttribute } from '../../types/firestore';

/**
 * Transform init data to match Firestore expected structure
 * This handles name-based references by looking up or creating entities by name
 */
export const transformInitData = async (data: Record<string, any[]>, logFn?: LogFunction): Promise<Record<string, any[] | Record<string, any>>> => {
  const transformedData: Record<string, any[] | Record<string, any>> = { ...data };
  
  try {
    logFn?.('Starting data transformation for Firestore import');
    
    // Create maps for flavors, attributes, and brands for lookups
    const flavorMap = new Map<string, string>(); // id -> name
    const flavorNameMap = new Map<string, string>(); // name -> id
    const attributeMap = new Map<string, string>(); // id -> name
    const attributeNameMap = new Map<string, string>(); // name -> id
    const brandMap = new Map<string, string>(); // id -> name
    const brandNameMap = new Map<string, string>(); // name -> id
    const drinkMap = new Map<string, string>(); // id -> name
    const drinkNameMap = new Map<string, string>(); // name -> id
    const locationMap = new Map<string, string>(); // id -> name
    const locationNameMap = new Map<string, string>(); // name -> id
    
    // Helper functions to get data in a consistent format
    const getCollection = (collectionName: string): any[] => {
      if (Array.isArray(data[collectionName])) {
        return data[collectionName];
      } else if (data[collectionName] && typeof data[collectionName] === 'object') {
        return Object.values(data[collectionName]);
      }
      return [];
    };
    
    // Load existing data from Firestore for reference resolution
    logFn?.('Loading existing data from Firestore for reference resolution');
    
    // Load existing flavors
    try {
      const flavorsSnapshot = await getDocs(collection(db, 'flavors'));
      flavorsSnapshot.forEach(doc => {
        const flavor = doc.data();
        if (flavor.name) {
          flavorMap.set(doc.id, flavor.name);
          flavorNameMap.set(flavor.name.toLowerCase(), doc.id);
        }
      });
      logFn?.(`Loaded ${flavorMap.size} existing flavors from Firestore`);
    } catch (error) {
      logFn?.('Warning: Failed to load existing flavors from Firestore');
    }
    
    // Load existing attributes
    try {
      const attributesSnapshot = await getDocs(collection(db, 'attributes'));
      attributesSnapshot.forEach(doc => {
        const attribute = doc.data();
        if (attribute.name) {
          attributeMap.set(doc.id, attribute.name);
          attributeNameMap.set(attribute.name.toLowerCase(), doc.id);
        }
      });
      logFn?.(`Loaded ${attributeMap.size} existing attributes from Firestore`);
    } catch (error) {
      logFn?.('Warning: Failed to load existing attributes from Firestore');
    }
    
    // Load existing brands
    try {
      const brandsSnapshot = await getDocs(collection(db, 'brands'));
      brandsSnapshot.forEach(doc => {
        const brand = doc.data();
        if (brand.name) {
          brandMap.set(doc.id, brand.name);
          brandNameMap.set(brand.name.toLowerCase(), doc.id);
        }
      });
      logFn?.(`Loaded ${brandMap.size} existing brands from Firestore`);
    } catch (error) {
      logFn?.('Warning: Failed to load existing brands from Firestore');
    }
    
    // Load existing drinks
    try {
      const drinksSnapshot = await getDocs(collection(db, 'drinks'));
      drinksSnapshot.forEach(doc => {
        const drink = doc.data();
        if (drink.name) {
          drinkMap.set(doc.id, drink.name);
          drinkNameMap.set(drink.name.toLowerCase(), doc.id);
        }
      });
      logFn?.(`Loaded ${drinkMap.size} existing drinks from Firestore`);
    } catch (error) {
      logFn?.('Warning: Failed to load existing drinks from Firestore');
    }
    
    // Load existing locations
    try {
      const locationsSnapshot = await getDocs(collection(db, 'locations'));
      locationsSnapshot.forEach(doc => {
        const location = doc.data();
        if (location.name) {
          locationMap.set(doc.id, location.name);
          locationNameMap.set(location.name.toLowerCase(), doc.id);
        }
      });
      logFn?.(`Loaded ${locationMap.size} existing locations from Firestore`);
    } catch (error) {
      logFn?.('Warning: Failed to load existing locations from Firestore');
    }
    
    // Fill flavor maps from import data
    const flavors = getCollection('flavors');
    flavors.forEach((flavor: Partial<Flavor>) => {
      if (flavor.id && flavor.name) {
        flavorMap.set(flavor.id, flavor.name);
        flavorNameMap.set(flavor.name.toLowerCase(), flavor.id);
      }
    });
    logFn?.(`Created lookup map for ${flavorMap.size} total flavors`);
    
    // Fill attribute maps from import data
    const attributes = getCollection('attributes');
    attributes.forEach((attribute: Partial<Attribute>) => {
      if (attribute.id && attribute.name) {
        attributeMap.set(attribute.id, attribute.name);
        attributeNameMap.set(attribute.name.toLowerCase(), attribute.id);
      }
    });
    logFn?.(`Created lookup map for ${attributeMap.size} total attributes`);
    
    // Fill brand maps from import data
    const brands = getCollection('brands');
    brands.forEach((brand: Partial<Brand>) => {
      if (brand.id && brand.name) {
        brandMap.set(brand.id, brand.name);
        brandNameMap.set(brand.name.toLowerCase(), brand.id);
      }
    });
    logFn?.(`Created lookup map for ${brandMap.size} total brands`);
    
    // Fill location maps from import data
    const locations = getCollection('locations');
    locations.forEach((location: Partial<FirestoreLocation>) => {
      if (location.id && location.name) {
        locationMap.set(location.id, location.name);
        locationNameMap.set(location.name.toLowerCase(), location.id);
      }
    });
    logFn?.(`Created lookup map for ${locationMap.size} total locations`);
    
    // Fill drink maps from import data
    const drinks = getCollection('drinks');
    drinks.forEach((drink: Partial<Drink>) => {
      if (drink.id && drink.name) {
        drinkMap.set(drink.id, drink.name);
        drinkNameMap.set(drink.name.toLowerCase(), drink.id);
      }
    });
    logFn?.(`Created lookup map for ${drinkMap.size} total drinks`);
    
    // Generate temporary IDs for items without IDs
    // This will help with cross-referencing during the import process
    // These IDs will be replaced during the actual import
    
    // Generate IDs for flavors without IDs
    flavors.forEach((flavor: Partial<Flavor>, index: number) => {
      if (!flavor.id && flavor.name) {
        const tempId = `temp_flavor_${index}`;
        flavor.id = tempId;
        flavorMap.set(tempId, flavor.name);
        flavorNameMap.set(flavor.name.toLowerCase(), tempId);
      }
    });
    
    // Generate IDs for attributes without IDs
    attributes.forEach((attribute: Partial<Attribute>, index: number) => {
      if (!attribute.id && attribute.name) {
        const tempId = `temp_attribute_${index}`;
        attribute.id = tempId;
        attributeMap.set(tempId, attribute.name);
        attributeNameMap.set(attribute.name.toLowerCase(), tempId);
      }
    });
    
    // Generate IDs for brands without IDs
    brands.forEach((brand: Partial<Brand>, index: number) => {
      if (!brand.id && brand.name) {
        const tempId = `temp_brand_${index}`;
        brand.id = tempId;
        brandMap.set(tempId, brand.name);
        brandNameMap.set(brand.name.toLowerCase(), tempId);
      }
    });
    
    // Generate IDs for locations without IDs
    locations.forEach((location: Partial<FirestoreLocation>, index: number) => {
      if (!location.id && location.name) {
        const tempId = `temp_location_${index}`;
        location.id = tempId;
        locationMap.set(tempId, location.name);
        locationNameMap.set(location.name.toLowerCase(), tempId);
      }
    });
    
    // Transform drink objects
    const drinksToTransform = getCollection('drinks');
    const transformedDrinks = drinksToTransform.map((drink: Partial<Drink>, index: number): Partial<Drink> => {
      const transformedDrink: Partial<Drink> = { ...drink };
      
      if (!transformedDrink.id) {
        transformedDrink.id = `temp_drink_${index}_${Date.now()}`;
        if (transformedDrink.name) {
          drinkMap.set(transformedDrink.id, transformedDrink.name);
          drinkNameMap.set(transformedDrink.name.toLowerCase(), transformedDrink.id);
        }
      }
      
      // Handle brand resolution (using brandId or brandName from Partial<Drink>)
      if (transformedDrink.brandId) {
        transformedDrink.brandName = brandMap.get(transformedDrink.brandId) || transformedDrink.brandName || 'Unknown Brand';
      } else if (transformedDrink.brandName) {
        const brandIdByName = brandNameMap.get(transformedDrink.brandName.toLowerCase());
        if (brandIdByName) {
          transformedDrink.brandId = brandIdByName;
        } else {
          logFn?.(`Warning: Brand name "${transformedDrink.brandName}" not found for drink "${transformedDrink.name}". A new brand might be created or lookup might fail if ID is also missing.`);
          // Optionally create a temp brandId if strict ID presence is needed later
          // transformedDrink.brandId = `temp_brand_for_${transformedDrink.brandName}`;
        }
      }
      // Ensure brandSize is valid or default
      transformedDrink.brandSize = transformedDrink.brandSize && ['craft', 'mass', 'unknown'].includes(transformedDrink.brandSize) ? transformedDrink.brandSize : 'unknown';

      if (Array.isArray(transformedDrink.flavors)) {
        transformedDrink.flavors = transformedDrink.flavors.map((flavorInput: any): DrinkFlavor => { // Input can be string or Partial<DrinkFlavor>
          if (typeof flavorInput === 'string') {
            const flavorId = flavorNameMap.get(flavorInput.toLowerCase());
            if (flavorId) return { id: flavorId, name: flavorInput, groupId: 0 }; // groupId as default
            logFn?.(`Warning: Flavor name "${flavorInput}" not found for drink "${transformedDrink.name}". Creating with placeholder ID.`);
            return { id: `unknown_flavor_${flavorInput.replace(/\s+/g, '_')}`, name: flavorInput, groupId: 0 };
          } else if (flavorInput && typeof flavorInput === 'object') {
            const id = flavorInput.id || flavorNameMap.get(String(flavorInput.name).toLowerCase()) || `temp_flavor_${String(flavorInput.name).replace(/\s+/g, '_')}`;
            const name = flavorInput.name || flavorMap.get(id) || 'Unknown Flavor';
            return { id, name: String(name), groupId: typeof flavorInput.groupId === 'number' ? flavorInput.groupId : 0 };
          }
          logFn?.(`Warning: Invalid flavor input for drink "${transformedDrink.name}": ${JSON.stringify(flavorInput)}. Skipping.`);
          return { id: 'invalid_flavor_skipped', name: 'Invalid Flavor Skipped', groupId: 0 }; // Fallback to satisfy DrinkFlavor
        }).filter(f => f.id !== 'invalid_flavor_skipped') as DrinkFlavor[];
      } else {
        transformedDrink.flavors = []; // Ensure it's an array
      }

      if (Array.isArray(transformedDrink.attributes)) {
        transformedDrink.attributes = transformedDrink.attributes.map((attrInput: any): DrinkAttribute => { // Input can be string or Partial<DrinkAttribute>
          if (typeof attrInput === 'string') {
            const attrId = attributeNameMap.get(attrInput.toLowerCase());
            if (attrId) return { id: attrId, name: attrInput, groupId: 0 };
            logFn?.(`Warning: Attribute name "${attrInput}" not found for drink "${transformedDrink.name}". Creating with placeholder ID.`);
            return { id: `unknown_attr_${attrInput.replace(/\s+/g, '_')}`, name: attrInput, groupId: 0 };
          } else if (attrInput && typeof attrInput === 'object') {
            const id = attrInput.id || attributeNameMap.get(String(attrInput.name).toLowerCase()) || `temp_attr_${String(attrInput.name).replace(/\s+/g, '_')}`;
            const name = attrInput.name || attributeMap.get(id) || 'Unknown Attribute';
            return { id, name: String(name), groupId: typeof attrInput.groupId === 'number' ? attrInput.groupId : 0 };
          }
          logFn?.(`Warning: Invalid attribute input for drink "${transformedDrink.name}": ${JSON.stringify(attrInput)}. Skipping.`);
          return { id: 'invalid_attr_skipped', name: 'Invalid Attribute Skipped', groupId: 0 }; // Fallback
        }).filter(a => a.id !== 'invalid_attr_skipped') as DrinkAttribute[];
      } else {
        transformedDrink.attributes = []; // Ensure it's an array
      }
      
      // Add computed fields if missing
      if (transformedDrink.averageRating === undefined) {
        transformedDrink.averageRating = 0;
      }
      if (transformedDrink.reviewCount === undefined) {
        transformedDrink.reviewCount = 0;
      }
      if (transformedDrink.sightingCount === undefined) {
        transformedDrink.sightingCount = 0;
      }
      
      return transformedDrink;
    });
    
    transformedData.drinks = transformedDrinks as Drink[]; // Final assertion
    
    // Transform sightings to use name-based references
    const sightings = getCollection('sightings');
    
    const transformedSightings = sightings.map((sighting: Partial<Sighting>, index: number) => {
      const transformedSighting: Partial<Sighting> = { ...sighting };
      
      // Generate temp ID if missing
      if (!transformedSighting.id) {
        transformedSighting.id = `temp_sighting_${index}`;
      }
      
      // Handle location name-based reference
      if (sighting.locationName && !transformedSighting.locationId) {
        const locationName = sighting.locationName;
          
        if (locationName) {
          const locationId = locationNameMap.get(locationName.toLowerCase());
          if (locationId) {
            transformedSighting.locationId = locationId;
          } else {
            logFn?.(`Warning: Location "${locationName}" not found for sighting "${transformedSighting.id}"`);
          }
        }
      }
      
      // Handle drink name-based reference
      if (sighting.drinkName && !transformedSighting.drinkId) {
        const drinkName = sighting.drinkName;
          
        if (drinkName) {
          const drinkId = drinkNameMap.get(drinkName.toLowerCase());
          if (drinkId) {
            transformedSighting.drinkId = drinkId;
          } else {
            logFn?.(`Warning: Drink "${drinkName}" not found for sighting "${transformedSighting.id}"`);
          }
        }
      }
      
      // Handle brand name-based reference
      const sightingWithBrandName = sighting as Partial<Sighting & { brandName?: string }>;
      if (sightingWithBrandName.brandName && !transformedSighting.brandId) { 
        const brandName = sightingWithBrandName.brandName;
          
        if (brandName) {
          const brandId = brandNameMap.get(brandName.toLowerCase());
          if (brandId) {
            transformedSighting.brandId = brandId;
          } else {
            logFn?.(`Warning: Brand "${brandName}" not found for sighting "${transformedSighting.id}"`);
          }
        }
      }
      
      return transformedSighting;
    });
    
    // Transform reviews to use name-based references
    const reviews = getCollection('reviews');
    
    const transformedReviews = reviews.map((review: Partial<Review>, index: number) => {
      const transformedReview: Partial<Review> = { ...review };
      
      // Generate temp ID if missing
      if (!transformedReview.id) {
        transformedReview.id = `temp_review_${index}`;
      }
      
      // Handle drink name-based reference
      if (review.drinkName && !transformedReview.drinkId) {
        const drinkName = review.drinkName;
          
        if (drinkName) {
          const drinkId = drinkNameMap.get(drinkName.toLowerCase());
          if (drinkId) {
            transformedReview.drinkId = drinkId;
          } else {
            logFn?.(`Warning: Drink "${drinkName}" not found for review "${transformedReview.id}"`);
          }
        }
      }
      
      // Handle location name-based reference
      if (review.locationName && !transformedReview.locationId) {
        const locationName = review.locationName;
          
        if (locationName) {
          const locationId = locationNameMap.get(locationName.toLowerCase());
          if (locationId) {
            transformedReview.locationId = locationId;
          } else {
            logFn?.(`Warning: Location "${locationName}" not found for review "${transformedReview.id}"`);
          }
        }
      }
      
      return transformedReview;
    });
    
    // Update the data structure based on the original format
    if (Array.isArray(data.sightings)) {
      transformedData.sightings = transformedSightings;
    } else if (data.sightings && typeof data.sightings === 'object') {
      transformedData.sightings = transformedSightings.reduce((acc, s) => {
        if (s.id) acc[s.id] = s as Sighting;
        return acc;
      }, {} as Record<string, Sighting>);
    }
    
    // Update reviews
    if (Array.isArray(data.reviews)) {
      transformedData.reviews = transformedReviews;
    } else if (data.reviews && typeof data.reviews === 'object') {
      transformedData.reviews = transformedReviews.reduce((acc, r) => {
        if (r.id) acc[r.id] = r as Review;
        return acc;
      }, {} as Record<string, Review>);
    }
    
    logFn?.(`Transformed data to match Firestore schema with proper references`);
    
    return transformedData;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error transforming initialization data:', error);
    logFn?.(`Error during data transformation: ${errorMessage}`);
    return data; // Return original data if transformation fails
  }
};

/**
 * Transforms raw Firestore export data into a more user-friendly format with name references
 * This is used to generate data that matches our import guide format
 */
export const transformExportDataToNameBased = (data: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  // First copy collections that don't need transformation
  const copyAsIs = ['flavors', 'attributes', 'brands', 'locations', 'permissions', 'users'];
  copyAsIs.forEach(collection => {
    if (data[collection]) {
      result[collection] = Object.values(data[collection]);
    }
  });
  
  // Transform drinks to use name references instead of IDs
  if (data.drinks) {
    const drinks = Object.values(data.drinks).map((drink: any) => {
      const transformed = { ...drink };
      
      // Use brand name directly instead of brandId
      if (transformed.brandId && data.brands?.[transformed.brandId]) {
        transformed.brand = data.brands[transformed.brandId].name;
        delete transformed.brandId;
        delete transformed.brandName; // Remove denormalized field
      }
      
      // Transform flavors to array of names
      if (Array.isArray(transformed.flavors)) {
        transformed.flavors = transformed.flavors.map((flavor: any) => flavor.name);
      }
      
      // Transform attributes to array of names
      if (Array.isArray(transformed.attributes)) {
        transformed.attributes = transformed.attributes.map((attribute: any) => attribute.name);
      }
      
      return transformed;
    });
    
    result.drinks = drinks.reduce((obj, drink) => { obj[drink.id] = drink; return obj; }, {});
  }
  
  // Transform sightings to use name references
  if (data.sightings) {
    const sightings = Object.values(data.sightings).map((sighting: any) => {
      const transformed = { ...sighting };
      
      // Use location name instead of locationId
      if (transformed.locationId && data.locations?.[transformed.locationId]) {
        transformed.location = data.locations[transformed.locationId].name;
        delete transformed.locationId;
        delete transformed.locationName; // Remove denormalized field
      }
      
      // Use drink name instead of drinkId
      if (transformed.drinkId && data.drinks?.[transformed.drinkId]) {
        transformed.drink = data.drinks[transformed.drinkId].name;
        delete transformed.drinkId;
        delete transformed.drinkName; // Remove denormalized field
      }
      
      // Use brand name instead of brandId
      if (transformed.brandId && data.brands?.[transformed.brandId]) {
        transformed.brand = data.brands[transformed.brandId].name;
        delete transformed.brandId;
      }
      
      return transformed;
    });
    
    result.sightings = sightings.reduce((obj, item) => { obj[item.id] = item; return obj; }, {});
  }
  
  // Transform reviews to use name references
  if (data.reviews) {
    const reviews = Object.values(data.reviews).map((review: any) => {
      const transformed = { ...review };
      
      // Use drink name instead of drinkId
      if (transformed.drinkId && data.drinks?.[transformed.drinkId]) {
        transformed.drink = data.drinks[transformed.drinkId].name;
        delete transformed.drinkId;
        delete transformed.drinkName; // Remove denormalized field
      }
      
      // Use location name instead of locationId
      if (transformed.locationId && data.locations?.[transformed.locationId]) {
        transformed.location = data.locations[transformed.locationId].name;
        delete transformed.locationId;
        delete transformed.locationName; // Remove denormalized field
      }
      
      return transformed;
    });
    
    result.reviews = reviews.reduce((obj, item) => { obj[item.id] = item; return obj; }, {});
  }
  
  // Copy other collections as is
  Object.keys(data).forEach(key => {
    if (!result[key]) {
      result[key] = data[key];
    }
  });
  
  return result;
}; 